// Contact support (topic + message, optional screenshot) and a short FAQ.
// Sends to the same support-request endpoint as before.
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { apiClient } from '@app/api/client';
import { Button, Card, Field, PageHeader, Select, Textarea } from '@app/components/ui';

export const SUPPORT_TOPICS = ['Roster and scheduling', 'Songs and setlists', 'Song Studio and Chord AI', 'Teams and invite codes', 'Billing', 'Something else'];

const FAQS: [string, string][] = [
  ['How do I move someone to a different service?', 'Open the service in Roster, remove them from that service and add them to the other one. Both changes notify them.'],
  ['Can members see the whole roster?', 'Members see the services they are scheduled for and the setlists for those services. Admins see everything.'],
  ['How do two worship teams stay separate?', 'Each team has its own roster, setlists and availability. Use the team switcher in the header to move between them.'],
  ['Can I import songs from another planner?', 'Yes. Upload a CSV with title, author, key, tempo and CCLI number from Songs.'],
];

const MAX_SCREENSHOT = 5 * 1024 * 1024;

export const Support: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [shot, setShot] = useState<{ data: string; name: string } | null>(null);
  const [errors, setErrors] = useState<{ topic?: string; message?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [faq, setFaq] = useState(-1);

  const pickShot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SCREENSHOT) {
      setErrors((x) => ({ ...x, form: 'Screenshots need to be under 5 MB' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setShot({ data: reader.result as string, name: file.name });
    reader.readAsDataURL(file);
  };

  const clearShot = () => {
    setShot(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!topic) next.topic = 'Pick a topic';
    if (!message.trim()) next.message = 'Tell us what happened';
    setErrors(next);
    if (next.topic || next.message) return;
    setBusy(true);
    try {
      await apiClient.post('/api/notifications/support-request', {
        category: topic,
        description: message.trim(),
        screenshot: shot?.data ?? null,
        userName: user?.username,
        userRole: user?.userRole,
        churchId: user?.churchId,
        churchName: user?.adminChurches?.find((c) => c.churchId === user?.churchId)?.churchName,
        appSource: 'admin-web',
        browserInfo: navigator.userAgent,
      });
      setSent(true);
      setTopic('');
      setMessage('');
      clearShot();
    } catch {
      setErrors({ form: "Your message didn't send. Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Help" title="How can we help?" />
      <div className="flex flex-wrap gap-3.5 items-start">
        {sent ? (
          <Card className="flex-[1_1_360px] flex flex-col items-center text-center gap-2 wp-pop" role="status">
            <span className="w-14 h-14 rounded-full bg-ok-bg text-ok flex items-center justify-center text-[22px] font-bold" aria-hidden="true">
              ✓
            </span>
            <div className="text-[17px] font-semibold mt-1">Message sent</div>
            <p className="text-[14px] text-t3 leading-[1.55]">
              We'll reply to <strong className="text-t1">{user?.email}</strong>.
            </p>
            <div className="flex gap-2 mt-2">
              <Button variant="ghost" onClick={() => setSent(false)}>
                Send another
              </Button>
              <Button variant="dark" onClick={() => navigate('/admin/dashboard')}>
                Back to home
              </Button>
            </div>
          </Card>
        ) : (
          <form className="flex-[1_1_360px] flex flex-col gap-3.5 rounded-[24px] border border-line bg-card p-[22px]" onSubmit={submit} noValidate>
            <h2 className="text-[15px] font-semibold">Contact support</h2>
            <Field label="Topic" htmlFor="sup-topic" error={errors.topic}>
              <Select id="sup-topic" value={topic} onChange={setTopic} placeholder="Choose a topic" invalid={!!errors.topic} options={SUPPORT_TOPICS.map((t) => ({ value: t, label: t }))} />
            </Field>
            <Field label="Message" htmlFor="sup-message" error={errors.message}>
              <Textarea id="sup-message" rows={5} value={message} invalid={!!errors.message} placeholder="Tell us what happened and what you expected." onChange={(e) => setMessage(e.target.value)} />
            </Field>
            <div className="flex items-center gap-2.5 text-[13px]">
              <input ref={fileRef} id="sup-shot" type="file" accept="image/*" className="sr-only" onChange={pickShot} />
              {shot ? (
                <>
                  <span className="text-t3 truncate flex-1">{shot.name}</span>
                  <Button variant="link" onClick={clearShot}>
                    Remove
                  </Button>
                </>
              ) : (
                <label htmlFor="sup-shot" className="text-link cursor-pointer hover:text-acc">
                  Attach a screenshot <span className="text-t5">· optional</span>
                </label>
              )}
            </div>
            {errors.form && (
              <div role="alert" className="text-[12.5px] text-acc">
                {errors.form}
              </div>
            )}
            <Button type="submit" shape="form" size="lg" loading={busy}>
              Send message
            </Button>
          </form>
        )}

        <Card pad="10px 22px" className="flex-[1_1_360px]">
          {FAQS.map(([q, a], i) => {
            const open = faq === i;
            return (
              <div key={q} className="border-b border-line last:border-0">
                <h3>
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={`faq-${i}`}
                    onClick={() => setFaq(open ? -1 : i)}
                    className="flex justify-between gap-3 w-full text-left py-4 text-[14.5px] font-semibold"
                  >
                    <span>{q}</span>
                    <span className="text-t4 text-[18px] leading-none" aria-hidden="true">
                      {open ? '−' : '+'}
                    </span>
                  </button>
                </h3>
                {open && (
                  <div id={`faq-${i}`} className="text-[14px] text-t3 leading-[1.6] pb-4 wp-fade">
                    {a}
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
};
